import { Button, DatePicker, Drawer, Form, Input, Modal, message } from 'antd';
import type { Dayjs } from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useEffect, useRef, useState } from 'react';

import { DATE } from '../Calendar/types';
import { queryErrorMessage } from '../CashTransactions/types';
import { type ClubMonth, defaultMonthName } from './types';

interface FormValues {
  name: string;
  range: [Dayjs, Dayjs];
}

interface Props {
  clubId: number;
  // null = closed; { month: null } = a new month starting on `range`.
  draft: { month: ClubMonth | null; range: [Dayjs, Dayjs] } | null;
  onClose: () => void;
  onSaved: () => void;
}

// Adds or changes a Club month. Its name follows its start date until it is
// typed over; shortening it asks before removing Sessions outside it.
export default function MonthDrawer({ clubId, draft, onClose, onSaved }: Props) {
  const { runQuery } = useScreen();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState(false);
  const nameTouched = useRef(false);
  const month = draft?.month ?? null;

  useEffect(() => {
    if (!draft) return;
    nameTouched.current = !!draft.month;
    form.setFieldsValue({
      name: draft.month?.name ?? defaultMonthName(draft.range[0]),
      range: draft.range,
    });
  }, [draft, form]);

  const submit = async (values: FormValues) => {
    setSaving(true);
    try {
      const rows = await runQuery<{ removed: number }>('club_month_save', {
        ...(month ? { id: month.id } : {}),
        clubId,
        name: values.name.trim(),
        startDate: values.range[0].format(DATE),
        endDate: values.range[1].format(DATE),
      });
      const removed = rows[0]?.removed ?? 0;
      message.success(
        removed
          ? `Хадгаллаа. ${removed} хичээллэх өдөр хасагдлаа.`
          : month
            ? 'Хадгаллаа'
            : 'Сар нэмэгдлээ',
      );
      onSaved();
    } catch (e) {
      message.error(queryErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    const values = await form.validateFields();
    const from = values.range[0].format(DATE);
    const to = values.range[1].format(DATE);
    const lost = month?.dates.filter((d) => d < from || d > to) ?? [];
    if (lost.length === 0) return submit(values);
    Modal.confirm({
      title: 'Хичээллэх өдөр хасагдана',
      content: `Шинэ хугацаанаас гадуурх ${lost.length} хичээллэх өдөр устна.`,
      okText: 'Хадгалах',
      cancelText: 'Болих',
      okButtonProps: { danger: true },
      onOk: () => submit(values),
    });
  };

  return (
    <Drawer
      open={!!draft}
      onClose={onClose}
      title={month ? month.name : 'Сар нэмэх'}
      size={480}
      destroyOnHidden
      extra={
        <Button type="primary" loading={saving} onClick={save}>
          Хадгалах
        </Button>
      }
    >
      <Form
        form={form}
        layout="vertical"
        requiredMark
        onFinish={save}
        onValuesChange={(changed: Partial<FormValues>) => {
          if ('name' in changed) nameTouched.current = true;
          if (changed.range?.[0] && !nameTouched.current) {
            form.setFieldValue('name', defaultMonthName(changed.range[0]));
          }
        }}
      >
        <Form.Item
          name="range"
          label="Хугацаа"
          rules={[{ required: true, message: 'Эхлэх, дуусах өдрөө сонгоно уу' }]}
        >
          <DatePicker.RangePicker className="w-full" format="YYYY.MM.DD" allowClear={false} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Нэр"
          rules={[{ required: true, whitespace: true, message: 'Нэрээ оруулна уу' }]}
        >
          <Input placeholder="2026 оны 9-р сар" />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
